/**
 * Formulista smooth scrolling.
 *
 * Eases mouse-wheel, keyboard and same-page anchor scrolling on the page scroll container
 * (`.page-wrapper` from 990px, the document below). Trackpads and touch keep their native
 * momentum, and anything the browser scrolls by itself (scrollbar drag, find in page, focus,
 * other scripts) takes over instantly.
 *
 * API: window.FMScroll.scrollTo(numberOrElement, { offset, duration, immediate }), .stop()
 */
(() => {
  if (window.FMScroll) return;

  const script = document.currentScript;
  const options = {
    feel: script?.dataset.feel || 'balanced',
    keys: script?.dataset.keys !== 'false',
    anchors: script?.dataset.anchors !== 'false',
  };

  // Damping rate per second: 95% of each wheel step is covered in ~3 / rate seconds.
  const RATES = { snappy: 14, balanced: 10, silky: 7 };
  const rate = RATES[options.feel] || RATES.balanced;

  const root = document.documentElement;
  const squeeze = window.matchMedia('(min-width: 990px)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const NESTED_OPT_OUT =
    '[data-fm-scroll-native], dialog[open], [aria-modal="true"], select, textarea, iframe, video';
  const KEY_OPT_OUT =
    'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="listbox"], [role="menu"], [role="menubar"], [role="slider"], [role="spinbutton"], [role="tablist"], [role="grid"], dialog[open], [data-fm-scroll-native]';
  const SPACE_OPT_OUT = 'a[href], button, summary, label, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])';

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const easeInOutQuart = (t) => (t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2);

  const getScroller = () =>
    (squeeze.matches && document.querySelector('.page-wrapper')) || document.scrollingElement || root;

  const isDocument = (element) => element === document.scrollingElement || element === root;
  const maxScroll = (element) => Math.max(0, element.scrollHeight - element.clientHeight);
  const toDevicePixel = (value) => {
    const ratio = window.devicePixelRatio || 1;
    return Math.round(value * ratio) / ratio;
  };

  const isActive = () => finePointer.matches && !reducedMotion.matches;

  const isLocked = (element) => {
    if (root.hasAttribute('scroll-lock')) return true;
    if (isDocument(element)) {
      return getComputedStyle(root).overflowY === 'hidden' || getComputedStyle(document.body).overflowY === 'hidden';
    }
    return getComputedStyle(element).overflowY === 'hidden';
  };

  const hasModal = () => {
    try {
      return Boolean(document.querySelector('dialog:modal'));
    } catch (_) {
      return Boolean(document.querySelector('dialog[open]'));
    }
  };

  let scroller = null;
  let current = 0;
  let target = 0;
  let tween = null;
  let frame = 0;
  let lastTime = 0;
  let written = null;
  let fineGestureAt = 0;
  let pendingFocus = null;

  const sync = () => {
    scroller = getScroller();
    current = target = scroller.scrollTop;
  };

  const write = (value) => {
    written = toDevicePixel(value);
    scroller.scrollTop = written;
  };

  const finish = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    tween = null;
    if (pendingFocus) {
      const element = pendingFocus;
      pendingFocus = null;
      element.focus({ preventScroll: true });
    }
  };

  const stop = () => {
    pendingFocus = null;
    finish();
    if (scroller) sync();
  };

  const tick = (now) => {
    const dt = lastTime ? clamp((now - lastTime) / 1000, 0.001, 0.05) : 1 / 60;
    lastTime = now;
    const limit = maxScroll(scroller);

    if (tween) {
      if (tween.start === null) tween.start = now - 1000 / 60;
      const progress = clamp((now - tween.start) / tween.duration, 0, 1);
      target = clamp(tween.to, 0, limit);
      current = tween.from + (target - tween.from) * easeInOutQuart(progress);
      if (progress >= 1) tween = null;
    } else {
      target = clamp(target, 0, limit);
      current += (target - current) * (1 - Math.exp(-rate * dt));
      if (Math.abs(target - current) < 0.4) current = target;
    }

    write(current);

    if (tween || current !== target) frame = requestAnimationFrame(tick);
    else finish();
  };

  const run = () => {
    if (frame) return;
    lastTime = 0;
    frame = requestAnimationFrame(tick);
  };

  const prepare = () => {
    const element = getScroller();
    if (element !== scroller || !frame) {
      finish();
      scroller = element;
      current = target = element.scrollTop;
    }
    return element;
  };

  const nudge = (delta) => {
    prepare();
    if (tween) {
      tween = null;
      target = current;
    }
    target = clamp(target + delta, 0, maxScroll(scroller));
    run();
  };

  const scrollTo = (destination, { offset = 0, duration, immediate = false } = {}) => {
    const element = prepare();
    let top = destination;
    if (destination instanceof Element) {
      const containerTop = isDocument(element) ? 0 : element.getBoundingClientRect().top;
      const margin = parseFloat(getComputedStyle(destination).scrollMarginTop) || 0;
      top = destination.getBoundingClientRect().top - containerTop + element.scrollTop - margin;
    }
    top = clamp(Number(top) - offset, 0, maxScroll(element));

    if (immediate || !isActive()) {
      finish();
      element.scrollTo({ top, behavior: immediate ? 'instant' : 'smooth' });
      return;
    }

    const distance = Math.abs(top - current);
    tween = {
      from: current,
      to: top,
      start: null,
      duration: duration ?? clamp(420 + Math.sqrt(distance) * 14, 520, 1400),
    };
    target = top;
    run();
  };

  const canNestedScroll = (node, delta, boundary) => {
    let element = node instanceof Element ? node : node?.parentElement;
    for (; element && element !== boundary && element !== document.body && element !== root; element = element.parentElement) {
      if (element.matches(NESTED_OPT_OUT)) return true;
      if (element.scrollHeight <= element.clientHeight + 1) continue;
      const style = getComputedStyle(element);
      if (!/(auto|scroll|overlay)/.test(style.overflowY)) continue;
      const room = delta > 0 ? element.scrollTop + element.clientHeight < element.scrollHeight - 1 : element.scrollTop > 0;
      if (room || style.overscrollBehaviorY !== 'auto') return true;
    }
    return false;
  };

  const onWheel = (event) => {
    if (event.defaultPrevented || event.ctrlKey || !isActive()) return;

    let delta = event.deltaY;
    if (!delta || event.shiftKey || Math.abs(event.deltaX) > Math.abs(delta)) return;
    if (event.deltaMode === 1) delta *= 40;
    else if (event.deltaMode === 2) delta *= getScroller().clientHeight;

    // Trackpads and free-spinning wheels send streams of small deltas with their own
    // inertia; the whole gesture is left native so it never feels floaty.
    const now = performance.now();
    if (Math.abs(delta) < 40 || now - fineGestureAt < 140) {
      fineGestureAt = now;
      if (frame) stop();
      return;
    }

    const element = getScroller();
    if (!isDocument(element) && !element.contains(event.target)) return;
    if (hasModal() || isLocked(element)) return;
    if (canNestedScroll(event.target, delta, element)) return;

    event.preventDefault();
    nudge(delta);
  };

  const onKey = (event) => {
    if (!options.keys || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !isActive()) return;
    const origin = event.target instanceof Element ? event.target : null;
    if (origin && (origin.isContentEditable || origin.closest(KEY_OPT_OUT))) return;

    const element = getScroller();
    if (hasModal() || isLocked(element)) return;
    if (origin && !isDocument(element) && origin !== document.body && !element.contains(origin)) return;

    const page = element.clientHeight * 0.88;
    let delta = 0;
    switch (event.key) {
      case 'ArrowDown':
        delta = 110;
        break;
      case 'ArrowUp':
        delta = -110;
        break;
      case 'PageDown':
        delta = page;
        break;
      case 'PageUp':
        delta = -page;
        break;
      case ' ':
        if (origin && origin.closest(SPACE_OPT_OUT)) return;
        delta = event.shiftKey ? -page : page;
        break;
      case 'Home':
        event.preventDefault();
        scrollTo(0);
        return;
      case 'End':
        event.preventDefault();
        scrollTo(maxScroll(element));
        return;
      default:
        return;
    }

    if (origin && canNestedScroll(origin, delta, element)) return;
    event.preventDefault();
    nudge(delta);
  };

  const onClick = (event) => {
    if (!options.anchors || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !isActive()) return;
    const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!link || link.hasAttribute('data-fm-scroll-native') || (link.target && link.target !== '_self')) return;

    const url = new URL(link.href, location.href);
    if (!url.hash || url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;

    const id = decodeURIComponent(url.hash.slice(1));
    const destination = id === 'top' ? 0 : document.getElementById(id);
    if (destination === null) return;
    if (isLocked(getScroller())) return;

    event.preventDefault();
    if (location.hash !== url.hash) history.pushState(null, '', url.hash);
    scrollTo(destination);

    if (destination instanceof Element) {
      if (!destination.matches('a[href], button, input, select, textarea, [tabindex]')) {
        destination.setAttribute('tabindex', '-1');
      }
      pendingFocus = destination;
    }
  };

  const onScroll = (event) => {
    if (!scroller) return;
    const source = event.target === document ? document.scrollingElement : event.target;
    if (source !== scroller) return;
    if (written !== null && Math.abs(scroller.scrollTop - written) <= 1.5) return;
    written = null;
    if (frame) finish();
    current = target = scroller.scrollTop;
  };

  const applyMode = () => {
    root.classList.toggle('fm-smooth', isActive());
    stop();
  };

  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKey);
  document.addEventListener('click', onClick);
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  [squeeze, finePointer, reducedMotion].forEach((query) => query.addEventListener('change', applyMode));
  window.addEventListener('pagehide', stop);

  applyMode();

  window.FMScroll = {
    scrollTo,
    stop,
    get scroller() {
      return getScroller();
    },
  };
})();
